// src/pages/super-admin/Tests/EditTest.tsx
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  useUpdateTestTypeMutation,
  type TestType,
  type TestTypeRequest,
} from "@/features/tests/services/testApi";
import { useListTestCategoriesQuery } from "@/api/masterDataApi";
import { toast } from "sonner";

interface EditTestProps {
  test: TestType;
  onSuccess: (test: TestType) => void;
  onClose: () => void;
}

export default function EditTest({ test, onSuccess, onClose }: EditTestProps) {
  const [formData, setFormData] = useState<TestTypeRequest>({
    testName: test.testName || "",
    testDescription: test.testDescription || "",
    testCategory: test.testCategory || "",
    status: test.status || "ACTIVE",
  });

  const [updateTest, { isLoading }] = useUpdateTestTypeMutation();
  const { data: categories, isLoading: catLoading } = useListTestCategoriesQuery();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.testName.trim() || !formData.testCategory.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      console.log("Sending update payload:", formData);
      const result = await updateTest({
        id: test.id,
        data: formData,
      }).unwrap();
      toast.success("Test type updated successfully");
      onSuccess(result);
      onClose();
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Failed to update test type");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Test Name */}
      <div>
        <label className="block text-sm font-medium mb-2">Test Name *</label>
        <Input
          name="testName"
          value={formData.testName}
          onChange={handleInputChange}
          disabled={isLoading}
          required
        />
      </div>

      {/* Category */}
      <div>
        <label className="block text-sm font-medium mb-2">Category *</label>
        <select
          name="testCategory"
          value={formData.testCategory}
          onChange={handleSelectChange}
          disabled={isLoading || catLoading}
          required
          className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">Select Category</option>
          {categories?.map((cat: any) => (
            <option key={cat.id} value={cat.name}>
              {cat.name}
            </option>
          ))}
        </select>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium mb-2">Description</label>
        <textarea
          name="testDescription"
          value={formData.testDescription}
          onChange={handleTextAreaChange}
          disabled={isLoading}
          className="w-full border border-gray-300 rounded px-3 py-2 min-h-[100px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Status */}
      <div>
        <label className="block text-sm font-medium mb-2">Status</label>
        <select
          name="status"
          value={formData.status}
          onChange={handleSelectChange}
          className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          disabled={isLoading}
        >
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
        </select>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 justify-end pt-4">
        <Button variant="outline" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="submit" className="bg-blue-600" disabled={isLoading}>
          {isLoading ? "Updating..." : "Update Test"}
        </Button>
      </div>
    </form>
  );
}
